import { UseUserQuery_user as UseUserQueryUser } from '../generated/types/UseUserQuery';
import { CONFIG } from '../config';

export enum AuthAction {
  Register = 'REGISTER',
  SignIn = 'SIGN IN'
}

export type AuthAttributes = {
  access_token?: string;
  email?: string;
  link_accounts?: boolean;
  password?: string;
  provider?: string;
  recaptcha?: AuthRecaptcha;
  cart_id?: string | null;
  guest?: boolean;
  first_name?: string;
  last_name?: string;
  opted_in?: boolean;
  user_loyalty_status?: number;
  status_level?: number;
};
export type AuthError = {
  code: string;
  detail?: string;
  message?: string;
};
export type AuthErrorMessage = string;
type AuthErrorMessages = {
  [errorCode: string]: AuthErrorMessage;
};
type AuthMeta = {
  access_token: string;
};
type AuthParams = {
  action: AuthAction;
  attributes: AuthAttributes;
  bypassRackAttack?: string;
};
export type AuthPayload = Omit<UseUserQueryUser, '__typename' | 'isLoggedIn' | 'oauth'> & {
  cartId: number | null;
};
export type AuthRecaptcha = {
  token: string;
  type: 'v2' | 'v3';
};
type AuthRequestBody = {
  data: {
    attributes: AuthAttributes;
    type: string;
  };
};
type AuthRequestConfig = {
  path: string;
  type: string;
};
type AuthRequestConfigs = {
  [action: string]: AuthRequestConfig;
};
export type AuthResponse = AuthResponseSuccess & AuthResponseError;
export type AuthResponseSuccess = {
  data: {
    attributes: {
      first_name?: string;
      last_name?: string;
      email: string;
      is_member: boolean;
      country_code?: string;
      client_tier?: number | null;
      cart_id?: number | null;
      is_amazon_connected?: boolean;
      is_facebook_connected?: boolean;
      user_loyalty_status?: number;
      date_of_birth?: string | null;
      phone?: string | null;
      status_level?: number;
    };
    id: string;
  };
  meta: AuthMeta;
};
type AuthResponseError = {
  errors: AuthError[] | [];
};
type AuthHeaders = {
  [header: string]: string;
};

export const ERROR_MESSAGES: AuthErrorMessages = {
  account_exists: 'An account with that email already exists.',
  invalid_credentials: 'Email and password do not match.',
  recaptcha_failed: 'Please complete the Captcha to continue!',
  default: 'Something went wrong.'
};

const AUTH_REQUEST_CONFIGS: AuthRequestConfigs = {
  REGISTER: {
    path: '/users',
    type: 'user'
  },
  'SIGN IN': {
    path: '/login',
    type: 'login'
  }
};

const getHeaders = (bypassRackAttack?: string): AuthHeaders => {
  const headers: AuthHeaders = { 'Content-Type': 'application/vnd.api+json' };

  if (bypassRackAttack) {
    headers['X_BYPASS_RACK_ATTACK'] = bypassRackAttack;
  }

  return headers;
};

export const handleAuthSuccess = (data: AuthResponseSuccess): AuthPayload => {
  return {
    accessToken: data.meta.access_token,
    email: data.data.attributes.email,
    isMember: data.data.attributes.is_member ?? null,
    firstName: data.data.attributes.first_name ?? null,
    id: data.data.id,
    lastName: data.data.attributes.last_name ?? null,
    countryCode: data.data.attributes.country_code ?? null,
    clientTier: data.data.attributes.client_tier ?? null,
    cartId: data.data.attributes.cart_id ?? null,
    isAmazonConnected: data.data.attributes.is_amazon_connected ?? null,
    isFacebookConnected: data.data.attributes.is_facebook_connected ?? null,
    userLoyaltyStatus: data.data.attributes.user_loyalty_status ?? null,
    dateOfBirth: data.data.attributes.date_of_birth ?? null,
    phone: data.data.attributes.phone ?? null,
    statusLevel: data.data.attributes.status_level ?? null
  };
};

export const handleAuthError = (error: AuthError) => {
  error.message = ERROR_MESSAGES[error.code] || error.detail || ERROR_MESSAGES.default;

  throw error;
};

export const authenticate = ({ action, attributes, bypassRackAttack }: AuthParams) => {
  const { path, type } = AUTH_REQUEST_CONFIGS[action];

  const body: AuthRequestBody = {
    data: {
      attributes,
      type
    }
  };

  return fetch(`${CONFIG.USER_API_URL}${path}`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: getHeaders(bypassRackAttack),
    redirect: 'follow',
    referrer: 'no-referrer',
    body: JSON.stringify(body)
  })
    .then(response => response.json())
    .then((data: AuthResponse) => {
      if (data.meta && data.data) {
        return handleAuthSuccess(data as AuthResponseSuccess);
      } else if (data.errors && data.errors[0]) {
        handleAuthError(data.errors[0]);
      }

      throw new Error(ERROR_MESSAGES.default);
    });
};
